<template>
  <div
    class="MiniCartProduct w-full"
    :class="{
      'last:border-none border-b border-border-subtle py-12 desk:py-16 first:pt-0': !disableInteraction || isPalletDiscount,
      'pt-8 first:pt-0 last:pb-8': disableInteraction && !isPalletDiscount
    }"
  >
    <div
      v-if="!disableInteraction && isCheckout && item.quantity > item.onHandValue && !hideMeta && neededArea > 0 && !isPalletDiscount"
      class="w-full bg-attentionLight p-[10px] mb-12"
    >
      <div class="flex type-sm gap-8">
        <img
          src="/icons/info.svg"
          class="text-attentionDark w-18 h-18 relative top-2"
          alt=""
        >
        <div class="flex flex-col">
          <p class="text-attentionDark font-medium">
            {{ $t('miniCart.splitDelivery.text', { amount: neededArea +' '+ unitDisplay }) }}
          </p>
          <p v-if="item.onHandValue > 0" class="text-text-subtle">
            {{ $t('miniCart.splitDelivery.description') }}
          </p>
        </div>
      </div>
    </div>
    <div class="flex">
      <!-- Pointer-only duplicate of the name link below; hidden from AT -->
      <nuxt-link
        v-if="!disableInteraction && !isPalletDiscount"
        :to="productUrl(item.url)"
        aria-hidden="true"
        tabindex="-1"
        class="basis-80 h-80 shrink-0 ratio-product relative flex items-center justify-center"
        :class="{
          'opacity-50': cartStore.isLoading,
        }"
      >
        <div v-if="item.image">
          <nuxt-img
            preset="standard"
            provider="norce"
            loading="lazy"
            sizes="sm:124px"
            class="h-full w-full object-cover aspect-product"
            :src="item.image"
            :class="{
              'max-h-32 max-w-32': isCustomerSuccessProduct
            }"
            :alt="item.variantName"
          />
          <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
        </div>
        <div v-else class="relative">
          <img
            src="/icons/no-image.svg"
            class="aspect-product w-64 rounded-6"
            alt=""
          >
          <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
        </div>
        <div
          v-if="item.priceInfo.priceBeforeVat === '0' && !isCustomerSuccessProduct && !isSample"
          class="absolute -top-6 -left-6 bg-lightest h-28 w-28 rounded-full flex items-center justify-center"
        >
          <div class="h-24 w-24 rounded-full bg-bg-attention flex items-center justify-center">
            <img
              class="w-16 h-16"
              src="/icons/gift.svg"
              alt=""
            >
          </div>
        </div>
      </nuxt-link>
      <div
        class="w-full flex justify-between"
        :class="{
          'ml-16': !disableInteraction && !isPalletDiscount
        }"
      >
        <div
          class="type-xs-medium desk:type-sm-medium flex flex-col justify-between"
          :class="{
            'opacity-50': cartStore.isLoading,
            'text-criticalDark': usedPoints > 0,
            'w-full -mr-[28px]': isCustomerSuccessProduct
          }"
        >
          <div>
            <span
              v-if="disableInteraction && !isPalletDiscount"
              :class="{
                'text-text-subtle type-sm font-normal': isCheckoutSummary,
              }"
            >
              {{ props.item.quantity }} x
            </span>
            <nuxt-link
              v-if="!disableInteraction && !isPalletDiscount"
              :to="productUrl(item.url)"
              :class="{
                'type-headline-sm': !isCheckoutSummary,
                'text-text-subtle type-sm font-normal': isCheckoutSummary,
              }"
            >
              {{ item.variantName }}
              <span v-if="shouldUseArea && isCheckoutSummary" class="">
                = {{ neededArea }} {{ unitDisplay }}
              </span>
            </nuxt-link>
            <span
              v-else
              :class="{
                'type-headline-sm': !isCheckoutSummary,
                'text-text-subtle type-sm font-normal': isCheckoutSummary,
              }"
            >
              {{ item.variantName }}
              <span v-if="shouldUseArea && isCheckoutSummary" class="">
                = {{ neededArea }} {{ unitDisplay }}
              </span>
            </span>

            <div v-if="!disableInteraction && !isPalletDiscount" class="type-xxs desk:type-xs text-text-subtle">
              {{ item.partNo }}
            </div>
          </div>
          <div
            v-if="isCustomerSuccessProduct"
            class="flex items-center w-full border border-light h-32 px-12 type-sm-medium"
          >
            {{ customerSuccessProductMessage }}
          </div>
          <div v-else-if="(!disableInteraction && !hideMeta) && !isPalletDiscount" class="flex items-top">
            <div v-if="(!globalStore.getIsB2c || item.isD2C) && !globalStore.getIsB2bSampleCheckout" class="relative flex items-center gap-12 mt-4">
              <PackageToAreaConverter
                v-if="shouldUseArea"
                :key="`converterKey${converterKey}`"
                v-model="quantity"
                :input="item"
                design="minicart"
                class="max-w-280"
                :disable-edit="disableEdit"
                :disabled="disabled"
                :attr="{ min: minimumAmount }"
              />

              <TouchNumber
                v-else
                id="quantitySelector"
                v-model="quantity"
                :show-buttons-on-desktop="true"
                design="sm"
                class="max-w-120"
                :disable-edit="disableEdit"
                :disabled="disabled"
                :attr="{ min: minimumAmount }"
              />
            </div>
            <div v-if="(!globalStore.getIsB2c || item.isD2C) && !globalStore.getIsB2bSampleCheckout">
              <div v-if="!isOrderProposal && isCheckout && !hideMultiDiscount">
                <MultiBuyCTA
                  :item="item"
                  :button-class="showDiscountBtn ? 'mb-8 !h-32':'!h-32'"
                  @update-quantity="updateQuantity"
                />
              </div>
            </div>
            <button
              v-if="!disableInteraction && !hideDelete && !disableEditing && !isPalletDiscount"
              class="basis-16 shrink-0"
              :class="{
                'ml-8': globalStore.getIsB2b
              }"
              type="button"
              :aria-label="$t('wcag.arialabel.removeFromCart', { name: item.variantName })"
              @click="remove"
            >
              <img
                src="/icons/trashcan.svg"
                class="w-16"
                alt=""
              >
            </button>
          </div>

          <div v-if="!disableInteraction && showCrossDockingInput && !hideMeta && globalStore.getIsB2b && !isPalletDiscount" class="flex mt-12 items-center">
            <input
              v-model="crossDockingValue"
              type="checkbox"
              class="basis-20 shrink-0"
              @click="toggleCrossDocking"
            >
            <span class="pl-12 type-sm-medium leading-single flex items-center">
              {{ $t('stock.sendWhenInStock') }} <span v-if="globalStore.crossDockingPrices?.priceBeforeVatDisplay" class="ml-2">(+{{ globalStore.crossDockingPrices.priceBeforeVatDisplay }})</span>
              <Tooltip
                class="ml-6"
                :text="$t('checkout.backorder.sendWhenInStock')"
                :show-on-hover="true"
              >
                <img
                  class="w-12 h-12 desk:w-16 desk:h-16 -translate-y-1"
                  src="/icons/info.svg"
                  alt=""
                >
              </Tooltip>
            </span>
          </div>

          <div
            v-if="lastError && lastErrorCode === 422 && !disableInteraction && Array.isArray(lastError?.errors) && lastError?.errors?.filter((e) => e.partNo === item.partNo).length"
            class="text-criticalDark mt-12"
          >
            <small>{{ $t('stock.noLongerInStock') }}</small>
          </div>
        </div>

        <div class="flex flex-col justify-between items-end">
          <div
            v-if="!isCustomerSuccessProduct"
            class="text-right"
            :class="{
              'opacity-50': cartStore.isLoading,
            }"
          >
            <!-- Always show standard package price first -->
            <div>
              <div
                class="type-headline-sm whitespace-nowrap"
                :class="{
                  'text-text-errorStrong': isPalletDiscount || usedPoints > 0 || (showStrikethrough && !disableInteraction),
                  'mt-12' : salesRepOfferDiscount > 0
                }"
              >
                {{ productPrice(item.priceInfoTotal, 'price', true) }}
              </div>
              <div
                v-if="showStrikethrough && !disableInteraction && !isPalletDiscount"
                class="type-xs text-text-subtle whitespace-nowrap line-through"
              >
                {{ productPrice(item.priceInfoTotal, 'priceBeforeDiscount', true) }}
              </div>

              <!-- Add price per area below if applicable -->
              <div v-if="showPricePerArea && pricePerArea" class="type-xs text-text-subtle mt-4">
                {{ useLocalizedCurrency(pricePerArea) }}/{{ unitDisplay }}
              </div>
            </div>

            <div
              v-if="salesRepOfferDiscount > 0 && (!isOrderProposal || (isOrderProposal && isCheckout))"
              class="type-xs-medium text-darker ml-4 mt-4"
            >
              <span v-if="userStore.isSalesRepUser && salesRepOfferDiscount === 100">
                {{ $t('checkout.gift') }}
              </span>
              <span v-else>
                {{ $t('productPage.price.yourDiscount') }} <span class="text-criticalDark">-{{ salesRepOfferDiscount.toFixed() }}%</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!disableInteraction && !hideMeta && (!globalStore.getIsB2c || item.isD2C) && !globalStore.getIsB2bSampleCheckout && !isPalletDiscount" class="flex items-center type-xs mt-12">
      <StockStatusSwitcher
        :cart-item="item"
        :is-checkout="isCheckout"
        :use-short-labels="true"
      />
    </div>
    <div
      v-if="isCheckout && !disableInteraction && !hideMeta && !isPalletDiscount"
      class="mt-4 type-sm text-text-base flex items-center gap-6"
    >
      <div v-if="showDatePickerInCheckout" class="w-full flex justify-start items-center gap-6">
        <span class="flex whitespace-nowrap">{{ $t('miniCart.preferredShippingDate') }}:</span>
        <client-only>
          <Datepicker
            v-if="isClientMounted && preferredShippingDate && defaultPreferredShippingDate"
            :key="`datepicker-${item.partNo}-${item.lineNo}`"
            :part-no="item.partNo"
            :date="preferredShippingDate"
            :default-date="defaultPreferredShippingDate"
            class="type-headline-sm flex items-center gap-4"
            @update-preferred-shipping-date="onUpdatePreferredShippingDateDebounced"
          />
        </client-only>
      </div>
    </div>
    <div v-if="isCheckout && !disableInteraction && !hideMeta && !globalStore.getIsB2c && !globalStore.getIsB2bSampleCheckout && !isPalletDiscount" class="type-xs mt-4">
      {{ $t('miniCart.preferredShippingDate.disclaimer') }}
    </div>

    <div
      v-if="(!isCheckout && !isCheckoutSummary && !isOrderProposal) && firstUnreachedQuantityBreak && firstUnreachedQuantityBreak.quantity - quantity > 0"
      class="type-xs mt-4 flex items-center gap-6"
    >
      <img
        src="/icons/info.svg"
        class="w-10"
        alt=""
      >
      {{ $t('miniCart.quantityBreak', {
        quantity: firstUnreachedQuantityBreak.quantity - quantity,
        discount: firstUnreachedQuantityBreak.discountPercentage
      }) }}
    </div>

    <div
      v-if="isPalletDiscount"
      class="type-xs text-text-base flex gap-6 mt-4"
    >
      {{ $t('checkout.palletDiscount') }}
    </div>

    <div
      v-if="isOrderProposal && !isCheckout && salesRepOfferDiscount === 100"
      class="type-xs text-text-base flex gap-6"
    >
      <img
        src="/icons/gift.svg"
        class="w-10"
        :alt="$t('checkout.gift')"
      >
      {{ $t('checkout.gift') }}
    </div>

    <div
      v-if="showDiscountBtn"
      class="mt-8 flex gap-8"
    >
      <img
        src="/icons/gift.svg"
        class="w-16"
        :alt="$t('checkout.orderProposal.editDiscount')"
      >
      <button
        class="btn btn--text flex items-center"
        type="button"
        @click="editProductDiscount(item)"
      >
        {{ $t('checkout.orderProposal.editDiscount') }}
      </button>
    </div>

    <div v-if="!disableInteraction && isCheckout && !hideMeta && !globalStore.getIsB2c && !globalStore.getIsB2bSampleCheckout" class="mt-12">
      <Field
        v-slot="{ field }"
        type="textarea"
        name="productComment"
      >
        <TextAreaInput
          v-bind="field"
          v-model="productComment"
          server-name="productComment"
          :placeholder="$t('checkout.item.extraComments')"
          :rows="1"
          @input="onUpdateCommentDebounced"
        />
      </Field>
    </div>

    <div v-if="usedPoints > 0" class="flex justify-between text-dark leading-single type-sm-medium mt-8">
      <div>{{ $t('pointshop.product.isPoints') }}</div>
      <div>{{ $t('pointshop.product.showPoints', { num: usedPoints }) }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCartStore } from '~/store/cart';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import type { CartApiItem } from '#constants/types/norce';
import useLinkReplacer from '~/composables/useLinkReplacer';
import debounce from '~/util/debounce';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import {
  NorceCartItemStatusId,
  NorceFlagCodes,
  NorceInfoTypesCodes
} from '#constants/norceCodes';
import MultiBuyCTA from '~/components/product-page/MultiBuyCTA.vue';
import useApiFetch from '~/composables/useApiFetch';
import Tooltip from '~/components/body/Tooltip.vue';
import usePrices from '~/composables/usePrices';
import { Field } from 'vee-validate';
import TextAreaInput from '~/components/form-elements/TextAreaInput.vue';
import TouchNumber from '~/components/form-elements/TouchNumber.vue';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import useQuantityBreaks from '~/composables/useQuantityBreaks';
import TextInput from '~/components/form-elements/TextInput.vue';
import Datepicker from '~/components/body/Datepicker.vue';
import NP from 'number-precision';
import StockStatusSwitcher from '~/components/product/StockStatusSwitcher.vue';
import {
  checkForFutureDelivery
} from '~/composables/useStockStatus';
import PackageToAreaConverter from '~/components/product-page/PackageToAreaConverter.vue';

NP.enableBoundaryChecking(false);

const { lastError, lastErrorCode } = useApiFetch();
const cartStore = useCartStore();
const uiStore = useUiStore();
const userStore = useUserStore();
const globalStore = useGlobalContentStore();
const { $t } = useNuxtApp();
const { productUrl } = useLinkReplacer();
const { productPrice } = usePrices();

const showStrikethrough = computed(() => {
  const info = props.item.priceInfoTotal;
  if (globalStore.getIsB2c) {
    return info.isCampaignPrice === true;
  }
  return info.price !== info.priceBeforeDiscount && (!globalStore.hideSRP || (info.discountPercentage ?? 0) > 0 || info.isCampaignPrice);
});

const route = useRoute();

const isCheckout = computed(()=> {
  return route.name?.toString()?.endsWith('checkout');
});

const props = defineProps<{
  item: CartApiItem,
  disableInteraction?: boolean,
  isOrderProposal?: boolean,
  showDiscountBtn?: boolean,
  hideDelete?: boolean,
  hideFavoriteAction?: boolean,
  hideMultiDiscount?: boolean,
  hideCrossDocking?: boolean,
  hideMeta?: boolean,
  isCheckoutSummary?: boolean,
  disableEdit?: boolean,
  disabled?: boolean,
  showPricePerArea?: boolean,
}>();

const { areaPerPackage, unitDisplay, shouldUseArea, convertPackageToArea, convertPackagePriceToAreaPrice } = usePackageAreaConverter(toRef(() => props.item));
const { checkDiscountLevels, firstUnreachedQuantityBreak } = useQuantityBreaks(toRef(() => props.item));

const pricePerArea = computed(() => {
  if (!shouldUseArea.value || !props.showPricePerArea || !props.item.priceInfo?.priceBeforeVat) {
    return null;
  }
  const price = parseFloat(props.item.priceInfo.priceBeforeVat.replaceAll(',', ''));
  return convertPackagePriceToAreaPrice(price);
});

onMounted(() => {
  checkDiscountLevels();
});

const quantity = ref<number>(props.item.quantity);
const selectQuantity = ref<number>(props.item.quantity);
const neededArea = ref(!shouldUseArea ? 0 : NP.times(quantity.value, (areaPerPackage.value ?? 0)));

const disableEditing = computed(() => {
  if (!userStore.isSalesRepUser && !userStore.isCustomerSuccessUser && props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.SalesRepDiscount)?.value) {
    return true;
  }

  return false;
});

const onDemand = computed(()=> {
  return props.item.flags.some((s) => s.code === NorceFlagCodes.OnDemand);
});

// Fallback 1:, if there are more in cart than on hand, dropdown goes to cart qty
const maxAvailable = computed(() => {
  if (globalStore.getAllowBackorders) {
    return 1000;
  }

  if (props.item.isRecommendedQtyFixed && (props.item.recommendedQty && props.item.recommendedQty > 0)) {
    const max = Math.max(props.item.quantity, props.item.onHandValue);
    if (max > props.item.recommendedQty) {
      return props.item.recommendedQty;
    }
  }

  const onHandValue = globalStore.getAllowBackorders && props.item.onHandValue === 0 && props.item.stockStatus.estimatedDelivery
    ? props.item.stockStatus.incomingValue
    : props.item.onHandValue;
  return parseInt(Math.max(props.item.quantity, onHandValue).toString(), 10);
});

const usedPoints = computed(()=>{
  const infoType = props.item.infoTypes.find((f) => f.name === NorceInfoTypesCodes.PointShopValue);
  if (infoType) {
    return parseInt(infoType.value);
  }
  return -1;
});

const isCustomerSuccessProduct = computed(() => {
  return props.item.infoTypes.find(infoType => infoType.name === 'isCustomerSuccessProduct')?.value === 'true';
});

const customerSuccessProductMessage = computed(() => {
  return props.item.infoTypes.find(infoType => infoType.name === 'customerSuccessProductMessage')?.value || '';
});

const isSample = computed(() => {
  return (props.item.categories.filter(c => c.code === 'Samples')?.length ?? 0) > 0;
});

const isPalletDiscount = computed(() => {
  return props.item.partNo === globalStore.palletDiscountPartNo;
});

const remove = () => {
  if (isCustomerSuccessProduct.value) {
    cartStore.updateCustomerSuccessProduct({ partNo: props.item.partNo, message: '' });
  } else {
    quantity.value = 0;
    updateQuantity(0);
  }
};

const isExpiring = computed(()=> {
  return props.item.statusId === NorceCartItemStatusId.Expiring;
});

const testoutput = ref(null) as any;

// * * * * * * * UPDATE CART
// i hade so much trouble with the debounce function, we use a regular timer
const timer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const isUpdating = ref(false);

watch(quantity, (value)=>{
  if (shouldUseArea.value) {
    neededArea.value = NP.times(quantity.value, areaPerPackage.value ?? 0);
  }
  if (!isCheckout.value) {
    checkDiscountLevels();
  }

  // Prevent duplicate updates
  if (isUpdating.value) {
    return;
  }

  clearTimeout(timer.value);
  timer.value = setTimeout(()=> updateQuantity(value), 500);
});

const converterKey = ref(1);

/**
 * Updates the quantity of an item in the cart based on the given input value.
 * This function includes validations and handles scenarios such as expiring products,
 * checking future delivery availability, and updating the cart through the API.
 *
 * @description
 * - If the current quantity matches the input, no action is taken.
 * - For products marked as expiring, it validates the availability using the API before proceeding.
 * - Displays appropriate error messages when quantity updates are restricted due to stock or expiration.
 * - Resets the quantity to its original value if the update fails.
 * - Updates the cart with the new quantity through the store when all validations pass.
 */
const updateQuantity = async(quantityInput: number) => {
  if (props.item.quantity === quantityInput || isUpdating.value) {
    return;
  }

  isUpdating.value = true;

  // If the product is expiring we need to check the api before
  // accepting any changes - this assumes a product can only be in cart on one lineNo, not duplicates
  if (isExpiring.value) {
    let needed = quantityInput;
    if (shouldUseArea.value) {
      needed = convertPackageToArea(quantityInput) || 1;
    }
    const futureRes = await checkForFutureDelivery(
      props.item.partNo,
      needed,
      0,
      shouldUseArea.value,
      unitDisplay,
      true
    );

    if (futureRes && futureRes.lockBuyButton) {
      testoutput.value = futureRes;
      let errorMsg = $t('stock.expiring');
      if (futureRes.totalAvailable && futureRes.unitDisplay) {
        errorMsg = $t('stock.expiring.max', {
          num: futureRes.totalAvailable,
          unit: futureRes.unitDisplay,
        });
      }

      uiStore.setTemporaryError(errorMsg, 5000);

    }
  }

  // Check so it's not under minimum value
  if (quantity.value < minimumAmount.value) {
    console.warn('less then minimum');
    // Resets quantity to before user interaction
    quantity.value = props.item.quantity;
    neededArea.value = NP.times(quantity.value, areaPerPackage.value ?? 0);
    isUpdating.value = false;
    return;
  }

  console.log('Updating');

  const res = await cartStore.updateCart(
    props.item.partNo,
    quantityInput,
    props.item.lineNo,
    false,
    props.item,
    true,
    '',
    props.item.priceListLocked
  );

  if (!res) {
    quantity.value = props.item.quantity;
    cartStore.isLoading = false;
  } else {
    const actualQuantity = cartStore.getQuantityFromLineNo(props.item.lineNo);
    if (actualQuantity !== quantityInput) {
      // This line in the cart didn't update to the requested quantityInput
      // This most likely happened because the backend split our order into multiple lines
      // Update the local quantity to reflect what the API actually returned for this line
      quantity.value = actualQuantity;
      if (shouldUseArea.value) {
        neededArea.value = NP.times(actualQuantity, areaPerPackage.value ?? 0);
      }
      converterKey.value++;
    }
  }

  isUpdating.value = false;
};

const editProductDiscount = (async(item: CartApiItem) => {
  uiStore.setCurrentlyEditingItem(item);
  uiStore.setShowEditProductDiscountModal(true);
});

const salesRepOfferDiscount = computed((): number => {
  return Number(props.item.infoTypes?.filter(i => i.name === NorceInfoTypesCodes.SalesRepDiscount)[0]?.value) * 100 || 0;
});

const showCrossDockingInput = computed(() => {
  return !cartStore.isOrderProposalBasket && props.item.onHandValue < 1 && !props.hideCrossDocking;
});

const crossDockingValue = computed(() => {
  return props.item.infoTypes?.filter(i => i.name === NorceInfoTypesCodes.CrossDocking)[0]?.value == 'true';
});

const toggleCrossDocking = async() => {
  const res = await cartStore.updateCart(
    props.item.partNo,
    props.item.quantity,
    props.item.lineNo,
    false,
    props.item,
    true,
    '',
    props.item.priceListLocked,
    true
  );

  if (!res) {
    console.log('res failed', res);
  }
};

// * * * * * * * minimum amount
const minimumAmount = computed(()=> {
  const min = props.item.infoTypes.find(f => f.name === 'salesRepMinQuantity');

  if (min) {
    return parseInt(min.value);
  }
  return 0;

});

// * * * * * * * UPDATE CART
const preferredShippingDate = ref(props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.PreferredShippingDate && i.value !== '')?.value);

const defaultPreferredShippingDate = ref(props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value);
const defaultPreferredShippingDateComputed = computed(() =>props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value);

const showDatePickerInCheckout = computed(() => {
  return globalStore.getIsB2b && !globalStore.getIsB2bSampleCheckout && defaultPreferredShippingDateComputed.value !== 'false';
});

// * * * * * * *  Watch if props are changed
watch(
  () => props.item,
  () => {
    if (!isUpdating.value) {
      quantity.value = props.item.quantity;
      selectQuantity.value = props.item.quantity;
    }
    preferredShippingDate.value = props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.PreferredShippingDate && i.value !== '')?.value;
    defaultPreferredShippingDate.value = props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value;

    if (!isCheckout.value) {
      checkDiscountLevels();
    }
  }
);

// Watch specifically for quantity changes from backend
watch(
  () => props.item.quantity,
  (newQuantity) => {
    if (newQuantity !== quantity.value && !isUpdating.value) {
      quantity.value = newQuantity;
      selectQuantity.value = newQuantity;
      if (shouldUseArea.value) {
        neededArea.value = NP.times(newQuantity, areaPerPackage.value ?? 0);
      }
    }
  }
);

const onUpdatePreferredShippingDateDebounced = debounce(async(date: string) => {
  await cartStore.updateCartItemInfoType(props.item, NorceInfoTypesCodes.PreferredShippingDate, date);

  if (cartStore.cart.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.MergeShipments && i.value === 'true')?.length) {
    await cartStore.updateCartInfoType(NorceInfoTypesCodes.MergeShipments, '');
  }
}, 500);

const productComment = ref(props.item.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.ShippingComment)[0]?.value ?? '');
const onUpdateCommentDebounced = debounce(async() => {
  await cartStore.updateCartItemInfoType(props.item, NorceInfoTypesCodes.ShippingComment, productComment.value);
}, 900);

// Prevent hydration issues with vue-date-picker in Nuxt 4
const isClientMounted = ref(false);
onMounted(() => {
  isClientMounted.value = true;
});

</script>
